import React, { useState } from 'react';
import { usePayments, useClients, useSuppliers, useBankAccounts, useInvalidate } from '../hooks/useApi';
import api from '../services/api';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

export default function Payments() {
  const [filter, setFilter] = useState({ type: '' });
  const { data: paymentsData, isLoading } = usePayments(filter);
  const { data: clients } = useClients();
  const { data: suppliers } = useSuppliers();
  const { data: bankAccounts } = useBankAccounts();
  const { invalidateAll } = useInvalidate();

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    payment_date: dayjs().format('YYYY-MM-DD'),
    type: 'client',
    client_id: '',
    supplier_id: '',
    bank_account_id: '',
    amount: '',
    notes: '',
  });

  const payments = paymentsData?.data || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (payload.type === 'client') {
        delete payload.supplier_id;
      } else {
        delete payload.client_id;
      }
      await api.post('/payments', payload);
      toast.success('Pagesa u regjistrua!');
      setShowModal(false);
      setForm({ payment_date: dayjs().format('YYYY-MM-DD'), type: 'client', client_id: '', supplier_id: '', bank_account_id: '', amount: '', notes: '' });
      invalidateAll();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gabim');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Jeni i sigurt? Kjo do rikthejë balancën.')) return;
    try {
      await api.delete(`/payments/${id}`);
      toast.success('Pagesa u fshi!');
      invalidateAll();
    } catch (error) {
      toast.error('Gabim');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Pagesat</h1>
        <button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2">
          <PlusIcon className="w-5 h-5" /> Regjistro Pagesë
        </button>
      </div>

      {/* Filter */}
      <div className="card">
        <div className="flex gap-4">
          <select value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })} className="input-field w-auto">
            <option value="">Të gjitha</option>
            <option value="client">Nga Klientët</option>
            <option value="supplier">Tek Furnitorët</option>
          </select>
        </div>
      </div>

      <div className="card">
        {isLoading ? (
          <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Data</th>
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Tipi</th>
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Klient/Furnitor</th>
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Llogaria</th>
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Shënim</th>
                  <th className="text-right py-3 px-2 font-medium text-gray-500">Shuma</th>
                  <th className="text-right py-3 px-2 font-medium text-gray-500">Veprime</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-2">{dayjs(payment.payment_date).format('DD/MM/YYYY')}</td>
                    <td className="py-3 px-2">
                      <span className={`px-2 py-1 rounded text-xs ${payment.type === 'client' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {payment.type === 'client' ? 'Arkëtim' : 'Pagesë'}
                      </span>
                    </td>
                    <td className="py-3 px-2">{payment.client?.name || payment.supplier?.name}</td>
                    <td className="py-3 px-2 text-gray-500">{payment.bank_account?.name}</td>
                    <td className="py-3 px-2 text-gray-500 text-sm">{payment.notes || '-'}</td>
                    <td className={`py-3 px-2 text-right font-medium ${payment.type === 'client' ? 'text-green-600' : 'text-red-600'}`}>
                      {payment.type === 'client' ? '+' : '-'}€{payment.amount}
                    </td>
                    <td className="py-3 px-2 text-right">
                      <button onClick={() => handleDelete(payment.id)} className="p-1 text-gray-500 hover:text-red-600">
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">Regjistro Pagesë</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipi</label>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, client_id: '', supplier_id: '' })} className="input-field">
                  <option value="client">Arkëtim nga Klienti</option>
                  <option value="supplier">Pagesë tek Furnitori</option>
                </select>
              </div>

              {form.type === 'client' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Klienti *</label>
                  <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="input-field" required>
                    <option value="">-- Zgjidh --</option>
                    {clients?.map(c => <option key={c.id} value={c.id}>{c.name} (€{c.balance?.toFixed(2)})</option>)}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Furnitori *</label>
                  <select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} className="input-field" required>
                    <option value="">-- Zgjidh --</option>
                    {suppliers?.map(s => <option key={s.id} value={s.id}>{s.name} (€{s.balance?.toFixed(2)})</option>)}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Llogaria Bankare *</label>
                <select value={form.bank_account_id} onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })} className="input-field" required>
                  <option value="">-- Zgjidh --</option>
                  {bankAccounts?.map(a => <option key={a.id} value={a.id}>{a.name} (€{a.balance})</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
                <input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="input-field" required />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shuma (€) *</label>
                <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="input-field" required />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shënim</label>
                <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-field" />
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Anulo</button>
                <button type="submit" className="btn-primary flex-1">Ruaj</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
